import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { authAPI } from '../api/axios'
import { disconnectEcho } from '../utils/echo'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user,    setUser]    = useState(null)
  const [loading, setLoading] = useState(true)
  const [token,   setToken]   = useState(() => localStorage.getItem('tabowe_token'))

  // ── Bootstrap: verify stored token on mount ──────────
  useEffect(() => {
    if (token) {
      authAPI.me()
        .then(({ data }) => setUser(data.data))
        .catch(() => {
          localStorage.removeItem('tabowe_token')
          localStorage.removeItem('tabowe_user')
          setToken(null)
        })
        .finally(() => setLoading(false))
    } else {
      setLoading(false)
    }
  }, [])

  // ── Login ─────────────────────────────────────────────
  const login = useCallback(async (credentials) => {
    const { data } = await authAPI.login(credentials)
    const { token: t, user: u } = data.data
    localStorage.setItem('tabowe_token', t)
    localStorage.setItem('tabowe_user', JSON.stringify(u))
    setToken(t)
    setUser(u)
    return u
  }, [])

  // ── Register ──────────────────────────────────────────
  const register = useCallback(async (payload) => {
    const { data } = await authAPI.register(payload)
    const { token: t, user: u } = data.data
    localStorage.setItem('tabowe_token', t)
    localStorage.setItem('tabowe_user', JSON.stringify(u))
    setToken(t)
    setUser(u)
    return u
  }, [])

  // ── Logout ────────────────────────────────────────────
  const logout = useCallback(async () => {
    try { await authAPI.logout() } catch (_) {}
    // Drop the realtime socket too — it authenticated with the old user's
    // token and would otherwise keep receiving their private-channel events
    // after sign-out.
    disconnectEcho()
    localStorage.removeItem('tabowe_token')
    localStorage.removeItem('tabowe_user')
    setToken(null)
    setUser(null)
  }, [])

  // ── Update local user ─────────────────────────────────
  const updateUser = useCallback((updates) => {
    setUser(prev => {
      const updated = { ...prev, ...updates }
      localStorage.setItem('tabowe_user', JSON.stringify(updated))
      return updated
    })
  }, [])

  // ── Token refresh — refresh every 6 days to stay ahead of 7-day expiry
  useEffect(() => {
    if (!token) return
    const SIX_DAYS = 6 * 24 * 60 * 60 * 1000
    const interval = setInterval(async () => {
      try {
        const { data } = await authAPI.refresh()
        const { token: t, user: u } = data.data
        localStorage.setItem('tabowe_token', t)
        localStorage.setItem('tabowe_user', JSON.stringify(u))
        setToken(t)
        setUser(u)
      } catch (_) {
        // Refresh failed — token likely expired, force logout
        localStorage.removeItem('tabowe_token')
        localStorage.removeItem('tabowe_user')
        setToken(null)
        setUser(null)
      }
    }, SIX_DAYS)
    return () => clearInterval(interval)
  }, [token])

  const loginWithToken = useCallback(async (token) => {
    localStorage.setItem('tabowe_token', token)
    setToken(token)

    const { data } = await authAPI.me()
    const userData = data.data
    localStorage.setItem('tabowe_user', JSON.stringify(userData))
    setUser(userData)
    return userData
  }, [])

  const value = {
    user,
    token,
    loading,
    isAuthenticated: !!token && !!user,
    isPlatformAdmin: !!user?.is_platform_admin,
    login,
    register,
    logout,
    loginWithToken,
    updateUser,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
